import { useEffect, useState } from "react";
import { Skeleton, Switch, Input, Button, Popconfirm, Popover } from "antd";
import { CheckOutlined, CopyOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  GetEventDetailService,
  UpdateWhatsappFlowStatus,
  UpdateWebFlowStatus,
  GetEventWebhookSettings,
  GenerateEventWebhookSecret,
} from "../../../../apis/events";
import { tostE, tostS } from "../../../../config/toastConfig";
import { PhotoHandoverCard } from "./Overview/PhotoHandoverCard";
import { RetentionStatusCard } from "./Overview/RetentionStatusCard";

export default function EventSettings() {
  const { id } = useParams();
  const { user } = useSelector((state) => state.auth);
  const [loading, setLoading] = useState(false);
  const [whatsappFlowEnabled, setWhatsappFlowEnabled] = useState(false);
  const [whatsappFlowLoading, setWhatsappFlowLoading] = useState(false);
  const [webFlowEnabled, setWebFlowEnabled] = useState(false);
  const [webFlowLoading, setWebFlowLoading] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState("");
  const [webhookSecret, setWebhookSecret] = useState("");
  const [webhookLoading, setWebhookLoading] = useState(false);
  const [webhookGenerating, setWebhookGenerating] = useState(false);
  const [copiedField, setCopiedField] = useState("");

  const getEventDetails = async () => {
    setLoading(true);
    try {
      const res = await GetEventDetailService({ id });
      if (res.status === 200 && res.data.success) {
        const { is_enable_whatsapp_flow, is_enable_web_flow } = res.data.event;
        setWhatsappFlowEnabled(!!is_enable_whatsapp_flow);
        setWebFlowEnabled(!!is_enable_web_flow);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const getWebhookSettings = async () => {
    setWebhookLoading(true);
    try {
      const res = await GetEventWebhookSettings({ event_id: id });
      if (res.status === 200 && res.data.success) {
        setWebhookUrl(res.data.data.webhook_url || "");
        setWebhookSecret(res.data.data.webhook_secret || "");
      }
    } catch (error) {
      console.log(error);
    } finally {
      setWebhookLoading(false);
    }
  };

  const isAdmin = user?.user_type === "admin";

  useEffect(() => {
    getEventDetails();
    if (isAdmin) {
      getWebhookSettings();
    }
  }, []); // eslint-disable-line

  const handleWhatsappFlowToggle = async (checked) => {
    if (user?.source === 'tktplz') {
      tostE("Please contact your Sales Representative or Event POC to get this feature enabled for your event.");
      return;
    }
    setWhatsappFlowLoading(true);
    try {
      const response = await UpdateWhatsappFlowStatus({ event_id: id, status: checked ? 1 : 0 });
      if (response.status === 200 && response.data.success) {
        tostS(response.data.message);
        setWhatsappFlowEnabled(checked);
      } else {
        tostE(response.data.message);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setWhatsappFlowLoading(false);
    }
  };

  const handleWebFlowToggle = async (checked) => {
    setWebFlowLoading(true);
    try {
      const response = await UpdateWebFlowStatus({ event_id: id, status: checked ? 1 : 0 });
      if (response.status === 200 && response.data.success) {
        tostS(response.data.message);
        setWebFlowEnabled(checked);
      } else {
        tostE(response.data.message);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setWebFlowLoading(false);
    }
  };

  const handleGenerateWebhookSecret = async () => {
    setWebhookGenerating(true);
    try {
      const response = await GenerateEventWebhookSecret({ event_id: id });
      if (response.status === 200 && response.data.success) {
        tostS(response.data.message);
        setWebhookUrl(response.data.data.webhook_url || "");
        setWebhookSecret(response.data.data.webhook_secret || "");
      } else {
        tostE(response.data.message);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setWebhookGenerating(false);
    }
  };

  const webhookSamplePayload = JSON.stringify(
    {
      id: "your-system-user-id-123",
      name: "John Doe",
      email: "john@example.com",
      country_code: "91",
      mobile: "9876543210",
      profile_image: "https://example.com/photo.jpg",
    },
    null,
    2
  );

  const webhookInfoContent = (
    <div style={{ maxWidth: 340 }}>
      <p className="mb-1" style={{ fontSize: "12px" }}>
        Send a <strong>POST</strong> request to the Webhook URL with header{" "}
        <code>X-Webhook-Secret: &lt;your webhook secret&gt;</code> and this JSON body:
      </p>
      <pre
        className="mb-1"
        style={{ fontSize: "11px", background: "#f5f5f5", padding: 8, borderRadius: 4, whiteSpace: "pre-wrap" }}
      >
        {webhookSamplePayload}
      </pre>
      <p className="mb-0 text-muted" style={{ fontSize: "11px" }}>
        name is required. At least one of email or mobile (with country_code) must be provided.
        profile_image is optional and must be a publicly accessible image URL. id is optional —
        your own user id (number or text, max 50 characters) — it is saved against this
        registration and returned back in the response.
      </p>
    </div>
  );

  const handleCopy = (value, field) => {
    navigator.clipboard
      .writeText(value)
      .then(() => {
        setCopiedField(field);
        setTimeout(() => setCopiedField(""), 2000);
      })
      .catch(() => tostE("Failed to copy!"));
  };

  return (
    <>
    <div className="card custom-card">
      {loading ? (
        <Skeleton loading active paragraph={{ rows: 3 }} />
      ) : (
        <>
          <div className="d-flex justify-content-between align-items-center pb-3 mb-1 border-bottom">
            <div>
              <p className="fw-500 mb-0">WhatsApp Version</p>
              <p className="text-muted mb-0" style={{ fontSize: "12px" }}>
                Enable WhatsApp flow for this event
              </p>
            </div>
            <Switch
              size="small"
              loading={whatsappFlowLoading}
              checked={whatsappFlowEnabled}
              onChange={handleWhatsappFlowToggle}
            />
          </div>

          <div className="d-flex justify-content-between align-items-center pt-3">
            <div>
              <p className="fw-500 mb-0">Web Version</p>
              <p className="text-muted mb-0" style={{ fontSize: "12px" }}>
                Enable web flow for this event
              </p>
            </div>
            <Switch
              size="small"
              loading={webFlowLoading}
              checked={webFlowEnabled}
              onChange={handleWebFlowToggle}
            />
          </div>

          {isAdmin && (
          <div className="pt-3 border-top mt-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div>
                <p className="fw-500 mb-0">
                  Webhook{" "}
                  <Popover content={webhookInfoContent} title="Webhook payload format" trigger="click">
                    <InfoCircleOutlined style={{ cursor: "pointer" }} />
                  </Popover>
                </p>
                <p className="text-muted mb-0" style={{ fontSize: "12px" }}>
                  Register users for this event from your own system by calling this URL with the
                  secret key below.
                </p>
              </div>
              <Popconfirm
                title={webhookSecret ? "Regenerate webhook secret?" : "Generate webhook secret?"}
                description={
                  webhookSecret
                    ? "Any system using the old secret will stop working."
                    : "This will create a webhook URL and secret for this event."
                }
                okText="Yes"
                cancelText="No"
                onConfirm={handleGenerateWebhookSecret}
              >
                <Button size="small" loading={webhookGenerating}>
                  {webhookSecret ? "Regenerate" : "Generate"}
                </Button>
              </Popconfirm>
            </div>

            {webhookLoading ? (
              <Skeleton loading active paragraph={{ rows: 2 }} />
            ) : webhookSecret ? (
              <>
                <div className="mb-2">
                  <p className="text-muted mb-1" style={{ fontSize: "12px" }}>
                    Webhook URL
                  </p>
                  <Input
                    readOnly
                    value={webhookUrl}
                    addonAfter={
                      <span
                        role="button"
                        onClick={() => handleCopy(webhookUrl, "url")}
                        style={{ cursor: "pointer" }}
                      >
                        {copiedField === "url" ? <CheckOutlined /> : <CopyOutlined />}
                      </span>
                    }
                  />
                </div>
                <div>
                  <p className="text-muted mb-1" style={{ fontSize: "12px" }}>
                    Webhook Secret
                  </p>
                  <Input.Password
                    readOnly
                    value={webhookSecret}
                    visibilityToggle
                    addonAfter={
                      <span
                        role="button"
                        onClick={() => handleCopy(webhookSecret, "secret")}
                        style={{ cursor: "pointer" }}
                      >
                        {copiedField === "secret" ? <CheckOutlined /> : <CopyOutlined />}
                      </span>
                    }
                  />
                </div>
              </>
            ) : (
              <p className="text-muted mb-0" style={{ fontSize: "12px" }}>
                No webhook configured yet. Click "Generate" to create one.
              </p>
            )}
          </div>
          )}
        </>
      )}
    </div>
    <PhotoHandoverCard id={id} user={user} />
    <RetentionStatusCard id={id} user={user} />
    </>
  );
}
